/**
 * Токенизация строки с меншенами.
 *
 * В тексте хранится только `[[<node-id>]]`, а человек не видит uuid НИКОГДА — ни при
 * чтении, ни при редактировании (ui-spec 8.4). Отсюда и вся эта механика: в `input`
 * лежит подставленный текст, рядом карта диапазонов, на сохранении — обратная подстановка.
 */

export type Token = {
	/** Индексы в ПОДСТАВЛЕННОМ тексте. */
	start: number;
	end: number;
	id: string;
	label: string;
};

const MENTION_RE = /\[\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]\]/gi;

/** Заглушка для цели, которой нет в реплике: архив не реплицируется, uuid не показываем. */
export const UNAVAILABLE = "↗ недоступно";

export function expand(raw: string, titleOf: (id: string) => string | undefined): { text: string; tokens: Token[] } {
	const tokens: Token[] = [];
	let text = "";
	let last = 0;

	for (const match of raw.matchAll(MENTION_RE)) {
		const at = match.index;
		text += raw.slice(last, at);
		const id = match[1]!.toLowerCase();
		const label = titleOf(id) ?? UNAVAILABLE;
		tokens.push({ start: text.length, end: text.length + label.length, id, label });
		text += label;
		last = at + match[0].length;
	}
	text += raw.slice(last);
	return { text, tokens };
}

/** Обратная подстановка: на сохранении токены снова становятся `[[id]]`. */
export function collapse(text: string, tokens: readonly Token[]): string {
	const sorted = [...tokens].sort((a, b) => a.start - b.start);
	let out = "";
	let cursor = 0;
	for (const token of sorted) {
		if (token.start < cursor) {
			continue;
		}
		out += text.slice(cursor, token.start);
		out += `[[${token.id}]]`;
		cursor = token.end;
	}
	return out + text.slice(cursor);
}

/**
 * Пересчёт карты после правки.
 *
 * Диапазон правки вычисляется общим префиксом и суффиксом: этого достаточно для любого
 * ввода, включая вставку из буфера в середину. Токен, который правка ЗАДЕЛА, считается
 * удалённым явно — частичной правки заголовка не бывает.
 */
export function remap(tokens: readonly Token[], prev: string, next: string): Token[] {
	let p = 0;
	const max = Math.min(prev.length, next.length);
	while (p < max && prev[p] === next[p]) {
		p += 1;
	}
	let s = 0;
	while (s < max - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) {
		s += 1;
	}

	const prevEnd = prev.length - s;
	const delta = next.length - prev.length;

	const out: Token[] = [];
	for (const token of tokens) {
		if (token.end <= p) {
			out.push(token);
		} else if (token.start >= prevEnd) {
			out.push({ ...token, start: token.start + delta, end: token.end + delta });
		}
		// иначе — правка задела токен, он удалён целиком
	}
	return out;
}

/** Токен, внутрь которого попал курсор (строго внутрь, не на границе). */
export function tokenAt(tokens: readonly Token[], caret: number): Token | undefined {
	return tokens.find((t) => caret > t.start && caret < t.end);
}

export function tokenEndingAt(tokens: readonly Token[], caret: number): Token | undefined {
	return tokens.find((t) => t.end === caret);
}

export function tokenStartingAt(tokens: readonly Token[], caret: number): Token | undefined {
	return tokens.find((t) => t.start === caret);
}

/**
 * Страховка от дыры в карте.
 *
 * Главный риск токенизированного `input` — неучтённый способ подвинуть курсор ломает
 * диапазон, и вместо меншена молча сохраняется обычный текст: ссылка исчезает без ошибки.
 * Поэтому меншен, пропавший НЕ по явному удалению токена, дописывается обратно —
 * худший исход дыры становится косметическим (текст разъехался с отображением),
 * а не потерей связи (ui-spec 8.5).
 */
export function preserveLostMentions(saved: string, before: readonly string[], removed: ReadonlySet<string>): string {
	const present = new Set([...saved.matchAll(MENTION_RE)].map((m) => m[1]!.toLowerCase()));
	const lost = before.filter((id) => !present.has(id) && !removed.has(id));
	return lost.length === 0 ? saved : `${saved} ${lost.map((id) => `[[${id}]]`).join(" ")}`;
}
